import { useState, useMemo } from "react";
import { parseISO } from "date-fns";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { DayTimeline } from "./DayTimeline";
import { TimeEntryDayGroup } from "./TimeEntryDayGroup";
import { TimeEntryDialog } from "./TimeEntryDialog";
import {
  groupEntriesByDay,
  formatDuration,
  toLocalDateString,
  getWeekRange,
  formatWeekLabel,
} from "@/lib/time-utils";
import { getEntries, createEntry, updateEntry, deleteEntry } from "@/lib/time-api";
import { ChevronLeft, ChevronRight, Loader2, Plus } from "lucide-react";
import type { TimeEntry, Project } from "@/types/time";

interface MemberTimeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  memberId: string;
  name: string;
  email: string;
  projects: Project[];
  projectColors: Map<string, string>;
}

export function MemberTimeDialog({
  open,
  onOpenChange,
  memberId,
  name,
  email,
  projects,
  projectColors,
}: MemberTimeDialogProps) {
  const queryClient = useQueryClient();
  const [weekOffset, setWeekOffset] = useState(0);
  const weekRange = getWeekRange(weekOffset);
  const weekLabel = formatWeekLabel(weekRange.start, weekRange.end);

  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [editEntry, setEditEntry] = useState<TimeEntry | null>(null);

  // Fetch entries via TanStack Query
  const { data, isLoading } = useQuery({
    queryKey: ["time-entries", "member", memberId, weekRange.start, weekRange.end],
    queryFn: () =>
      getEntries({
        member_id: memberId,
        start_date: weekRange.start,
        end_date: weekRange.end,
        per_page: 200,
      }),
    enabled: open && !!memberId,
  });

  const entries = data?.data ?? [];
  const dayGroups = useMemo(() => groupEntriesByDay(entries), [entries]);
  const weekTotal = entries.reduce((sum, e) => sum + (e.durationSeconds ?? 0), 0);

  const today = toLocalDateString(new Date());
  const todayEntries = entries.filter(
    (e) => toLocalDateString(parseISO(e.start)) === today
  );

  const initials = name
    ? name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : email?.[0]?.toUpperCase() ?? "?";

  function openCreateDialog() {
    setEditEntry(null);
    setEntryDialogOpen(true);
  }

  function openEditDialog(entry: TimeEntry) {
    setEditEntry(entry);
    setEntryDialogOpen(true);
  }

  function handleOpenChange(isOpen: boolean) {
    if (!isOpen) {
      setWeekOffset(0);
    }
    onOpenChange(isOpen);
  }

  async function handleEntrySubmit(data: {
    description?: string | null;
    projectId?: string | null;
    taskId?: string | null;
    start: string;
    end: string;
    createdWith?: string;
  }) {
    try {
      if (editEntry) {
        await updateEntry(editEntry.id, data);
        toast.success("Entry updated");
      } else {
        await createEntry({ ...data, createdWith: data.createdWith ?? "web", memberId });
        toast.success("Entry created");
      }
      await queryClient.invalidateQueries({ queryKey: ["time-entries"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save entry");
    }
  }

  async function handleDelete(id: string) {
    try {
      await deleteEntry(id);
      toast.success("Entry deleted");
      await queryClient.invalidateQueries({ queryKey: ["time-entries"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete entry");
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl w-[90vw] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="text-xs">{initials}</AvatarFallback>
            </Avatar>
            <div>
              <p>{name || email}</p>
              <p className="text-sm font-normal text-muted-foreground">{email}</p>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setWeekOffset((o) => o - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium min-w-[180px] text-center">
                {weekLabel}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setWeekOffset((o) => o + 1)}
                disabled={weekOffset >= 0}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              {weekOffset !== 0 && (
                <Button variant="ghost" size="sm" onClick={() => setWeekOffset(0)}>
                  Today
                </Button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">
                Total: {formatDuration(weekTotal)}
              </span>
              <Button size="sm" onClick={openCreateDialog}>
                <Plus className="mr-1 h-4 w-4" />
                Add Entry
              </Button>
            </div>
          </div>

          {weekOffset === 0 && todayEntries.length > 0 && (
            <DayTimeline
              entries={todayEntries}
              date={today}
              projectColors={projectColors}
            />
          )}

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : dayGroups.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No entries this week
            </p>
          ) : (
            <div className="space-y-6">
              {dayGroups.map((group) => (
                <TimeEntryDayGroup
                  key={group.date}
                  group={group}
                  onEdit={openEditDialog}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </div>

        <TimeEntryDialog
          open={entryDialogOpen}
          onOpenChange={(open) => {
            setEntryDialogOpen(open);
            if (!open) setEditEntry(null);
          }}
          entry={editEntry}
          defaultDate={toLocalDateString(parseISO(weekRange.start))}
          projects={projects}
          existingEntries={entries}
          onSubmit={handleEntrySubmit}
        />
      </DialogContent>
    </Dialog>
  );
}
